import { IntegrationReportRules } from '@app/model/integration-settings/Integration';
import { ruleCaptions } from '@app/model/integration-settings/ReportRules';
import omit from 'lodash/omit';
import type { DataTableBand, DataTableCellTone, DataTableColumn, DataTableColumnGroup, DataTableRow, DataTableSort } from '@ui/chart/DataTable';
import type { ColumnsPanelItem } from '@ui/menu/ColumnsPanel';
import type { DailyCompsetDay, DailyCut, DailyNewNight, DailyPerformance, RevenueScope, RevenueValues, StlyBasis, VatBasis } from '@app/model/bi/Bi';
import { revenueOf } from '../Bi.utilities';

// ROO2-288: the Daily Performance report (BI report 6): the figures of the night table, the totals and the columns (B10).
// Every ratio comes from sums, never from an average of nights; a missing value is null (the table shows "–"), never 0.
// The Excel export of the server (DailyPerformanceCells) holds the same rules; the fixture bi/daily-performance-cells.json
// tests both. A change of a rule needs the same change there.

export const PU_DAYS = [1, 3, 7, 14, 30] as const;

const iso = (d: Date) => d.toISOString().slice(0, 10);
const addDays = (isoDate: string, days: number) => iso(new Date(Date.parse(`${isoDate}T00:00:00Z`) + days * 86400000));

// ROO2-298 (D47–D52): the Nights range of the page replaces the Month stepper (D26) and Show (D10): a first and a last
// night, any day of the month. A quick pick is relative to the as-of day, so it moves with the days.
export type NightRange = { from: string, to: string };
export type RangePickId = 'thisMonth' | 'nextMonth' | 'next30' | 'next90' | 'next6m' | 'next12m';
// D49, B1: the longest range; the last night is before the first night plus 12 months (the java.time plusMonths rule).
export const MAX_RANGE_MONTHS = 12;
// D50: the last night that the calendar allows is the end of the 12th month after the month of the as-of day.
export const AHEAD_MONTHS = 12;

const lastOfMonth = (y: number, m: number) => iso(new Date(Date.UTC(y, m + 1, 0)));
// The date plus n months; a day that the shorter month does not have becomes its last day (java.time plusMonths).
export const plusMonths = (isoDate: string, n: number): string => {
	const y = Number(isoDate.slice(0, 4)), m = Number(isoDate.slice(5, 7)) - 1, d = Number(isoDate.slice(8, 10));
	const total = y * 12 + m + n, ty = Math.floor(total / 12), tm = total % 12;
	return iso(new Date(Date.UTC(ty, tm, Math.min(d, Number(lastOfMonth(ty, tm).slice(8, 10))))));
};

// D48: the quick picks.
export const RANGE_PICKS: { id: RangePickId, label: string }[] = [
	{ id: 'thisMonth', label: 'This month' }, { id: 'nextMonth', label: 'Next month' }, { id: 'next30', label: 'Next 30 nights' },
	{ id: 'next90', label: 'Next 90 nights' }, { id: 'next6m', label: 'Next 6 months' }, { id: 'next12m', label: 'Next 12 months' },
];
export const isRangePick = (value: string | null): value is RangePickId => RANGE_PICKS.some(p => p.id === value);

// The nights of a quick pick. This month and Next month are whole calendar months; "Next …" starts tonight (the as-of day,
// lead 0, E1). Next 6 (12) months = tonight to the night before the same date 6 (12) months later.
export const pickRange = (id: RangePickId, asOf: string): NightRange => {
	const y = Number(asOf.slice(0, 4)), m = Number(asOf.slice(5, 7)) - 1;
	switch (id) {
		case 'thisMonth': return { from: `${asOf.slice(0, 7)}-01`, to: lastOfMonth(y, m) };
		case 'nextMonth': return { from: iso(new Date(Date.UTC(y, m + 1, 1))), to: lastOfMonth(y, m + 1) };
		case 'next30': return { from: asOf, to: addDays(asOf, 29) };
		case 'next90': return { from: asOf, to: addDays(asOf, 89) };
		case 'next6m': return { from: asOf, to: addDays(plusMonths(asOf, 6), -1) };
		case 'next12m': return { from: asOf, to: addDays(plusMonths(asOf, MAX_RANGE_MONTHS), -1) };
	}
};

// D50: the dates that the calendar allows: from the hotel's first stay night (none before the first answer) to AHEAD_MONTHS
// months ahead.
export const rangeLimits = (asOf: string, firstStayNight: string | null | undefined): { min: string | undefined, max: string } => {
	const y = Number(asOf.slice(0, 4)), m = Number(asOf.slice(5, 7)) - 1;
	return { min: firstStayNight ?? undefined, max: lastOfMonth(y, m + AHEAD_MONTHS) };
};

// B1: a range that the API takes (from <= to and to < from + 12 months), inside the limits when they are given.
export const validRange = (r: NightRange, limits?: { min: string | undefined, max: string }): boolean =>
	/^\d{4}-\d{2}-\d{2}$/.test(r.from) && /^\d{4}-\d{2}-\d{2}$/.test(r.to) && r.from <= r.to && r.to < plusMonths(r.from, MAX_RANGE_MONTHS)
	&& (limits === undefined || ((limits.min === undefined || r.from >= limits.min) && r.to <= limits.max));

// The range is one whole calendar month: the titles keep the month names ("October 2026", "October total").
export const wholeMonth = (r: NightRange): boolean =>
	r.from.slice(8, 10) === '01' && r.from.slice(0, 7) === r.to.slice(0, 7)
	&& r.to === lastOfMonth(Number(r.to.slice(0, 4)), Number(r.to.slice(5, 7)) - 1);

export const nightsOf = (from: string, to: string): string[] => {
	const out: string[] = [];
	for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
	return out;
};

const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
export const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
// 0 = Sunday … 6 = Saturday.
export const dayOf = (night: string) => new Date(`${night}T00:00:00Z`).getUTCDay();
// ROO2-294: a weekend night is a Friday or a Saturday night (the guests leave on Sunday); the other five are weekday nights.
export const WEEKEND_DAYS = [5, 6];
export const WEEKDAY_DAYS = [0, 1, 2, 3, 4, 5, 6].filter(d => !WEEKEND_DAYS.includes(d));
// "Sun 25 Oct"; short (a phone, D33): "Sun 25".
export const nightLabel = (night: string, short = false) => short
	? `${WD[dayOf(night)]} ${night.slice(8, 10)}`
	: `${WD[dayOf(night)]} ${night.slice(8, 10)} ${MON[Number(night.slice(5, 7)) - 1]}`;
export const monthLabel = (month: string) => `${MONTH_LONG[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;
// D51: the note of a month row. A month at an end of the range holds only its nights in the range (all = those nights), and
// the note names them ("· 15–31 Dec · 17 nights"); a whole month gives "· 31 nights". shown = the nights that the filters show.
export const monthNote = (all: string[], shown: number): string => {
	const first = all[0], last = all[all.length - 1];
	const part = wholeMonth({ from: first, to: last }) ? '' : ` ${Number(first.slice(8, 10))}–${Number(last.slice(8, 10))} ${MON[Number(first.slice(5, 7)) - 1]} ·`;
	return `·${part} ${shown} night${shown === 1 ? '' : 's'}`;
};
// Days from today to the night; negative for a past night.
export const leadOf = (night: string, asOf: string) => Math.round((Date.parse(`${night}T00:00:00Z`) - Date.parse(`${asOf}T00:00:00Z`)) / 86400000);
// ROO2-289 G8: a night has a day popup when it is today or later and has a compset (status OK); a month row has none.
export const hasDayPopup = (night: string, asOf: string, days: Map<string, DailyCompsetDay>) =>
	night.length === 10 && leadOf(night, asOf) >= 0 && days.get(night)?.status === 'OK';
// ROO2-301 U2: the shown nights with a day popup, the nearest first (the page preloads the first 31).
export const popupNights = (shown: string[], asOf: string, days: Map<string, DailyCompsetDay>): string[] =>
	shown.filter(n => hasDayPopup(n, asOf, days)).sort();

// B3: the last-year night of a night: Day of week = 364 days earlier; Same date = the same date one year earlier, and a
// 29/02 has none (null).
export const lastYearNight = (night: string, basis: StlyBasis): string | null => {
	if (basis === 'WEEKDAY') return addDays(night, -364);
	return night.slice(5) === '02-29' ? null : `${Number(night.slice(0, 4)) - 1}${night.slice(4)}`;
};

// --- the figures of one night ---------------------------------------------------------------------------------------------
// ROO2-315 part b: occ = the occupied rooms of the report rules (= rn for a hotel with every default).
// ROO2-302: blk = the held rooms of group blocks in rn.
export type Fig = { rn: number, rev: number | null, occ: number, blk?: number };
const ZERO_VALUES: RevenueValues = { roomRevenue: 0, roomRevenueNet: 0, totalRevenue: 0, totalRevenueNet: 0 };
// held: the rooms that the PMS holds but that are no room night (blocked + on option, D38); lyMissing: there is no last-year
// night (a 29/02 on Same date) or it is before the hotel's first stay night, so STLY and Final have no value (B3, D40).
// news: ROO2-314 Part 3, the room nights and revenue of the NEW bookings of each PU block on the night (findDailyNewNights).
// avail: the available rooms of the night (rooms − its out-of-order rooms when the rule says so; null: no room count); ooo: the
// out-of-order rooms of the night (OPERA's statistics), shown in the Out of order column whatever the rule.
export type NightFigures = Record<DailyCut, Fig> & { held: number, lyMissing: boolean, news: Record<PuCut, Fig>, avail: number | null, availLy: number | null, ooo: number };
const CUTS: DailyCut[] = ['D0', 'D1', 'D3', 'D7', 'D14', 'D30', 'STLY', 'LY_ACTUAL'];
type PuCut = DailyNewNight['cut'];
const PU_CUTS: PuCut[] = PU_DAYS.map(p => `D${p}` as PuCut);

// The rows of findDailyPerformance as night → cut → figures in the revenue of the switches; a missing row is 0. A 29/02 on
// Same date has no last-year night (B3), so its STLY and Final are missing, as on the server. ROO2-314 Part 3: newNights are
// the rows of findDailyNewNights (empty while no New column is shown).
export const figuresByNight = (data: DailyPerformance, scope: RevenueScope, vat: VatBasis, newNights: DailyNewNight[] = []): Map<string, NightFigures> => {
	const out = new Map<string, NightFigures>();
	// ROO2-310 B6, Q3: with Include an option night from the as-of day is a room night, so it is not for sale only before that
	// day (a past option night never counts); Rooms left therefore does not change between the two states.
	const held = new Map(data.held.map(h => [h.night, h.blocked + (data.includeOptions && h.night >= data.asOf ? 0 : h.onOption)]));
	const available = new Map((data.available ?? []).map(a => [a.night, a]));
	nightsOf(data.stayFrom, data.stayTo).forEach(night => {
		const lastYear = lastYearNight(night, data.stlyBasis);
		out.set(night, {
			...Object.fromEntries(CUTS.map(c => [c, { rn: 0, occ: 0, rev: revenueOf(ZERO_VALUES, scope, vat) }])) as Record<DailyCut, Fig>,
			held: held.get(night) ?? 0,
			news: Object.fromEntries(PU_CUTS.map(c => [c, { rn: 0, occ: 0, rev: revenueOf(ZERO_VALUES, scope, vat) }])) as Record<PuCut, Fig>,
			avail: data.rooms === null ? null : (available.get(night)?.availableRooms ?? data.rooms),
			availLy: data.rooms === null ? null : (available.get(night)?.lastYearAvailableRooms ?? data.rooms),
			ooo: available.get(night)?.outOfOrder ?? 0,
			lyMissing: lastYear === null || (data.firstStayNight !== null && lastYear < data.firstStayNight),
		});
	});
	data.nights.forEach(r => {
		const n = out.get(r.night);
		if (n !== undefined) n[r.cut] = { rn: r.roomNights, occ: r.occupiedRooms ?? r.roomNights, rev: revenueOf(r, scope, vat), blk: r.heldBlockRooms ?? 0 };
	});
	newNights.forEach(r => {
		const n = out.get(r.night);
		if (n !== undefined) n.news[r.cut] = { rn: r.roomNights, occ: r.roomNights, rev: revenueOf(r, scope, vat) };
	});
	return out;
};

const ratio = (a: number | null, b: number | null): number | null => a === null || b === null || b === 0 ? null : a / b;
const minus = (a: number | null, b: number | null) => a === null || b === null ? null : a - b;
const plus = (a: number | null, b: number | null) => a === null || b === null ? null : a + b;
const change = (now: number | null, then: number | null) => now === null || then === null || then === 0 ? null : now / then - 1;
// ROO2-315 B7: ADR = revenue ÷ occupied rooms. New ADR stays revenue ÷ new room nights (part b Q4): it describes the bookings.
export const adr = (f: Fig) => ratio(f.rev, f.occ);
const bookingAdr = (f: Fig) => ratio(f.rev, f.rn);

export type Sum = { nights: number, future: number, left: number, held: number, lyMissing: boolean, cuts: Record<DailyCut, Fig>, news: Record<PuCut, Fig>,
	avail: number | null, availLy: number | null, ooo: number };
// Σ RN and Σ revenue of each cut; a revenue with one missing night is missing (ROO2-236 R2), and so is the last year of a
// total that holds a night with no last-year data (D40). Rooms left = Σ over the nights from today of rooms − RN − held (D38).
// ROO2-315: the available rooms of a total are the sum over its nights (sum ÷ sum, Q6 of round 1); Rooms left = Σ available −
// RN − held over the nights from today.
export const sumOf = (nights: string[], byNight: Map<string, NightFigures>, asOf: string, rooms: number | null): Sum => {
	const cuts = Object.fromEntries(CUTS.map(c => [c, { rn: 0, occ: 0, rev: 0 as number | null }])) as Record<DailyCut, Fig>;
	const news = Object.fromEntries(PU_CUTS.map(c => [c, { rn: 0, occ: 0, rev: 0 as number | null }])) as Record<PuCut, Fig>;
	let future = 0, left = 0, held = 0, lyMissing = false, avail: number | null = rooms === null ? null : 0, availLy: number | null = avail, ooo = 0;
	nights.forEach(night => {
		const n = byNight.get(night);
		if (n === undefined) return;
		CUTS.forEach(c => { cuts[c] = { rn: cuts[c].rn + n[c].rn, occ: cuts[c].occ + n[c].occ, rev: plus(cuts[c].rev, n[c].rev), blk: (cuts[c].blk ?? 0) + (n[c].blk ?? 0) }; });
		PU_CUTS.forEach(c => { news[c] = { rn: news[c].rn + n.news[c].rn, occ: news[c].occ + n.news[c].occ, rev: plus(news[c].rev, n.news[c].rev) }; });
		held += n.held;
		ooo += n.ooo;
		avail = plus(avail, n.avail);
		availLy = plus(availLy, n.availLy);
		lyMissing ||= n.lyMissing;
		if (leadOf(night, asOf) >= 0) { future += 1; left += (n.avail ?? 0) - n.D0.rn - n.held; }
	});
	return { nights: nights.length, future, left, held, lyMissing, cuts, news, avail, availLy, ooo };
};

// The nights above the rooms (RQ-42, D35): their dates, for the warning. ROO2-315 Q4: occupied rooms above available rooms.
export const overCapacity = (nights: string[], byNight: Map<string, NightFigures>, rooms: number | null): string[] =>
	rooms === null ? [] : nights.filter(night => { const n = byNight.get(night); return n !== undefined && n.avail !== null && n.D0.occ > n.avail; });

// --- the columns (RQ-13; D12, G8, D23, D33) -------------------------------------------------------------------------------
const BANDS: DataTableBand[] = [
	{ atLeast: 0.94, fill: 'green-strong' }, { atLeast: 0.84, fill: 'green' }, { atLeast: 0.74, fill: 'green-light' },
	{ atLeast: 0.6, fill: 'red-light' }, { atLeast: 0, fill: 'red' },
];
export type DailyColumn = DataTableColumn & { group: string };
// Night does not sort: DataTable would sort its labels ("Tue 13 Oct") as text. The table opens in night order. ROO2-289: the
// Compset column is "vs Median" or "vs W. avg" (compsetLabel, D20).
// The scan behind the Compset column: the default filter of the day popup (B2); nights = the hotel's first default nights.
export const scanSource = (nights: number) => `Booking.com, desktop, public price, ${nights} night${nights === 1 ? '' : 's'}, 2 guests`;

// ROO2-315 B10 (part b Q5): the Occupancy, ADR and RevPAR texts follow the hotel's report rules; every default keeps today's.
// ROO2-302 (D1): blocks = a night of the window has held rooms of group blocks; the Group blocks column then shows after RN.
export const dailyColumns = (rooms: number | null, compsetLabel = 'vs Median', nights = 1, rules: IntegrationReportRules | null = null,
	blocks = false): DailyColumn[] => {
	const caption = ruleCaptions(rules, rooms);
	return [
	{ group: '', id: 'night', label: 'Night', sortable: false },
	{ group: 'Today', id: 'lead', label: 'Lead', format: 'number', info: { title: 'Lead', content: 'Days from today (company time zone) to the night. Empty for a past night.' } },
	{ group: 'Today', id: 'occ', label: 'Occ%', format: 'percent', decimals: 1, tone: 'band', bands: BANDS, outlineAbove: 1,
		info: { title: 'Occupancy', content: `${caption.occupancy} 94 %, 84 % and 74 % and more in green; 60 % and under 60 % in red. Above 100 %: a red ring.` } },
	{ group: 'Today', id: 'rn', label: 'RN', format: 'number', info: blocks ? { title: 'RN', content: 'The room nights on the books, with the held rooms of group blocks (the Group blocks column).' } : undefined },
	...(blocks ? [{ group: 'Today', id: 'blocks', label: 'Group blocks', format: 'number' as const, info: { title: 'Group blocks',
		content: 'The rooms that the hotel holds for groups and that no guest booked yet, from the definite and tentative blocks of OPERA. They are in RN, '
			+ 'and their potential revenue is in Rev, as in OPERA\'s rooms sold, so they count in the occupancy, ADR and RevPAR. When a guest books from a '
			+ 'block, the guest is a room night and the block holds one room less. With Options: Include, the held rooms of option (non-deduct) blocks are '
			+ 'here too; with Exclude they are in Not for sale.' } }] : []),
	{ group: 'Today', id: 'rev', label: 'Rev', format: 'money',
		info: rules?.roomRevenue === 'ALL_ROOM_POSTINGS' ? { title: 'Revenue', content: 'All room postings of the night, as the PMS report: the room nights and the room revenue that OPERA posts outside them (day use, no-shows, cancellations, pseudo rooms, postings outside the stay).' } : undefined },
	{ group: 'Today', id: 'adr', label: 'ADR', format: 'money', info: rules === null ? undefined : { title: 'ADR', content: caption.adr } },
	{ group: 'Today', id: 'revpar', label: 'RevPAR', format: 'money', info: rules === null ? undefined : { title: 'RevPAR', content: caption.revpar } },
	{ group: 'Today', id: 'left', label: 'Left', format: 'number', info: { title: 'Rooms left', content: rules?.outOfOrderReducesRooms === true
		? 'Available rooms (rooms − out of order) − the room nights on the books − the rooms not for sale. Empty for a past night; negative above the rooms.'
		: 'Rooms − the room nights on the books − the rooms not for sale. Empty for a past night; negative above the rooms.' } },
	...(rules === null ? [] : [{ group: 'Today', id: 'ooo', label: 'Out of order', format: 'number' as const,
		info: { title: 'Out of order', content: rules.outOfOrderReducesRooms ? 'The rooms that OPERA marks out of order on the night (OPERA\'s statistics, the newest import). They leave the available rooms.' : 'The rooms that OPERA marks out of order on the night. The report rules keep them in the available rooms.' } }]),
	{ group: 'Today', id: 'held', label: 'Not for sale', format: 'number', info: { title: 'Not for sale', content: `${rules === null ? 'Rooms blocked (maintenance, out of order, owner use)'
		: 'Rooms blocked'} and rooms on option. They are not in the occupancy; Rooms left leaves them out. With Options: Include, a room on option from today is a room night, so only a past one is here.${
		rules === null ? '' : ' OPERA\'s out-of-order rooms are in the Out of order column only.'}` } },
	{ group: 'Compset', id: 'diff', label: compsetLabel, format: 'percent', decimals: 0, signed: true, tone: 'chip',
		info: { title: compsetLabel, content: `Your lowest price ÷ the compset value − 1, from the RoomPulse scan (${scanSource(nights)}). The colour follows the warning range of your RateShopper settings. Hover the chip of a future night for the day popup.` } },
	{ group: 'Previous year', id: 'stlyRn', label: 'STLY RN', format: 'number' },
	{ group: 'Previous year', id: 'stlyRev', label: 'STLY Rev', format: 'money' },
	{ group: 'Previous year', id: 'stlyD', label: 'Δ% STLY', format: 'percent', decimals: 1, signed: true, tone: 'change' },
	{ group: 'Previous year', id: 'finRn', label: 'Final RN', format: 'number' },
	{ group: 'Previous year', id: 'finRev', label: 'Final Rev', format: 'money' },
	{ group: 'Previous year', id: 'finD', label: 'Δ% Final', format: 'percent', decimals: 1, signed: true, tone: 'change' },
	...PU_DAYS.flatMap(p => [
		{ group: `Situation D-${p}`, id: `d${p}Rn`, label: 'RN', format: 'number' as const },
		{ group: `Situation D-${p}`, id: `d${p}Rev`, label: 'Rev', format: 'money' as const },
		{ group: `Situation D-${p}`, id: `d${p}Adr`, label: 'ADR', format: 'money' as const }]),
	...PU_DAYS.flatMap(p => [
		{ group: `PU ${p}`, id: `pu${p}Rn`, label: 'RN', format: 'number' as const, signed: true, tone: 'change' as const },
		{ group: `PU ${p}`, id: `pu${p}Rev`, label: 'Rev', format: 'money' as const, signed: true, tone: 'change' as const },
		{ group: `PU ${p}`, id: `pu${p}Adr`, label: 'ADR Δ', format: 'money' as const, signed: true, tone: 'change' as const },
		// ROO2-314 Part 3 (D29): the new bookings of the block only; never negative, so no sign and no tone.
		{ group: `PU ${p}`, id: `pu${p}NewRn`, label: 'New RN', format: 'number' as const,
			info: { title: `New RN (PU ${p})`, content: `The room nights of this night in the bookings made in the last ${p} ${p === 1 ? 'day' : 'days'} (on the books now). A booking made and cancelled in the window is left out.` } },
		{ group: `PU ${p}`, id: `pu${p}NewAdr`, label: 'New ADR', format: 'money' as const,
			info: { title: `New ADR (PU ${p})`, content: 'The revenue ÷ the room nights of those new bookings on this night. Cancellations do not enter it, unlike ADR Δ.' } }]),
	];
};

// ROO2-314 Part 3: a New RN or New ADR column; the page reads findDailyNewNights only while one is shown.
export const isNewColumn = (id: string) => /^pu\d+New(Rn|Adr)$/.test(id);

// The report and its New rows, read by two queries that each keep the previous window's answer while a new window loads.
export type DailyPair = { data: DailyPerformance | undefined, newRows: DailyNewNight[] | undefined };

// ROO2-314 Part 3: the table pairs the report with New rows of the same window. While one answer is still the previous
// window's (placeholder data) and the other is the new one, the dimmed table keeps the last pair that matched, so a night
// never shows the New figures of another hotel, range or Options choice. Without a New column only the report counts.
export const matchedPair = (current: DailyPair, last: DailyPair | undefined, newShown: boolean, reportPlaceholder: boolean, newPlaceholder: boolean): DailyPair => {
	const mixed = newShown && current.data !== undefined && current.newRows !== undefined && reportPlaceholder !== newPlaceholder;
	return mixed && last !== undefined ? last : current;
};

// The default columns (Today, Compset, Previous year without Final revenue, PU 7 with New RN and New ADR, ROO2-314 D29) and the
// phone set (D33).
export const DEFAULT_COLUMNS = ['night', 'lead', 'occ', 'rn', 'blocks', 'rev', 'adr', 'revpar', 'left', 'ooo', 'held', 'diff', 'stlyRn', 'stlyRev', 'stlyD', 'finRn', 'finD', 'pu7Rn', 'pu7Rev', 'pu7Adr',
	'pu7NewRn', 'pu7NewAdr'];
export const PHONE_COLUMNS = ['night', 'occ', 'adr', 'diff', 'pu7Rn'];
const PHONE_LABEL: Record<string, string> = { occ: 'Occ' };

// The columns of the table in the user's order (ColumnsPanel items), with the header groups.
export const tableColumns = (all: DailyColumn[], items: ColumnsPanelItem[], phone: boolean): { columns: DataTableColumn[], groups: DataTableColumnGroup[] } => {
	const byId = new Map(all.map(c => [c.id, c]));
	const shown = items.filter(i => i.visible).map(i => byId.get(i.id)).filter((c): c is DailyColumn => c !== undefined)
		.map(c => !phone ? c : { ...c, label: c.id === 'diff' ? c.label.replace('Median', 'Med') : (PHONE_LABEL[c.id] ?? c.label), info: undefined, decimals: c.id === 'occ' ? 0 : c.decimals });
	const groups: DataTableColumnGroup[] = [];
	shown.forEach(c => {
		const last = groups[groups.length - 1];
		if (last !== undefined && last.label === c.group) last.span += 1; else groups.push({ label: c.group, span: 1 });
	});
	return { columns: shown.map(c => omit(c, 'group')), groups };
};

// The name of a column in the Columns panel and in the export header: "Rev", "PU 7 RN".
export const panelLabel = (c: DailyColumn) => ['', 'Today', 'Compset', 'Previous year'].includes(c.group) ? c.label : `${c.group} ${c.label}`;

// The ColumnsPanel items: Night first and always shown (locked), then the saved order of the user. No saved list: every
// column in the table order, the defaults shown. ROO2-314 Part 3 (Q2): a column that the saved list does not know (a new
// column) goes after the last saved column of its group (at the end when the list has none), shown when it is a default.
// ROO2-302: Group blocks belongs right after RN, so a saved list that has RN but has never seen Group blocks gets it there.
export const panelItems = (all: DailyColumn[], saved: { id: string, visible: boolean }[] | null, defaults: string[]): ColumnsPanelItem[] => {
	const byId = new Map(all.map(c => [c.id, c]));
	let order: { id: string, visible: boolean }[];
	if (saved === null) order = all.map(c => ({ id: c.id, visible: defaults.includes(c.id) }));
	else {
		order = saved.filter(s => byId.has(s.id));
		all.filter(c => !saved.some(s => s.id === c.id)).forEach(c => {
			const rn = c.id === 'blocks' ? order.findIndex(o => o.id === 'rn') : -1;
			const after = rn >= 0 ? rn : order.map(o => byId.get(o.id)?.group).lastIndexOf(c.group);
			const item = { id: c.id, visible: defaults.includes(c.id) };
			if (after < 0 || c.group === '') order.push(item); else order.splice(after + 1, 0, item);
		});
	}
	const night = order.filter(o => o.id === 'night');
	return [...night, ...order.filter(o => o.id !== 'night')].map(({ id, visible }) => {
		const c = byId.get(id) as DailyColumn;
		return { id, label: panelLabel(c), visible: id === 'night' ? true : visible, group: c.group === '' ? undefined : c.group, locked: id === 'night' };
	});
};

// ROO2-302: the list to save from the panel. A saved column that the table does not offer now (Group blocks without held
// rooms, Out of order without report rules) keeps its place and choice: it goes after the column that came before it in the
// saved list, so it comes back as the user left it.
export const columnsToSave = (items: { id: string, visible: boolean }[], saved: { id: string, visible: boolean }[] | null): { id: string, visible: boolean }[] => {
	const next = items.map(({ id, visible }) => ({ id, visible }));
	const shown = new Set(next.map(i => i.id));
	(saved ?? []).forEach((s, index) => {
		if (shown.has(s.id)) return;
		const before = saved?.slice(0, index).map(p => p.id).filter(id => next.some(n => n.id === id)).pop();
		next.splice(before === undefined ? 0 : next.findIndex(n => n.id === before) + 1, 0, { id: s.id, visible: s.visible });
	});
	return next;
};

// --- the rows -------------------------------------------------------------------------------------------------------------
type Cells = Record<string, string | number | null>;
// ROO2-315 B7: occupancy = occupied ÷ available, RevPAR = revenue ÷ available (Q5); avail = Σ available rooms of the nights.
const figureCells = (s: Record<DailyCut, Fig>, avail: number | null, lyMissing: boolean, held: number, news: Record<PuCut, Fig>, ooo: number): Cells => {
	const t = s.D0;
	const ly = (v: number | null) => lyMissing ? null : v;
	const cells: Cells = {
		occ: ratio(t.occ, avail), rn: t.rn, blocks: (t.blk ?? 0) === 0 ? null : (t.blk ?? 0), rev: t.rev, adr: adr(t), revpar: ratio(t.rev, avail), held: held === 0 ? null : held, ooo: ooo === 0 ? null : ooo,
		stlyRn: ly(s.STLY.rn), stlyRev: ly(s.STLY.rev), stlyD: ly(change(t.rev, s.STLY.rev)),
		finRn: ly(s.LY_ACTUAL.rn), finRev: ly(s.LY_ACTUAL.rev), finD: ly(change(t.rev, s.LY_ACTUAL.rev)),
	};
	PU_DAYS.forEach(p => {
		const then = s[`D${p}` as DailyCut];
		cells[`d${p}Rn`] = then.rn; cells[`d${p}Rev`] = then.rev; cells[`d${p}Adr`] = adr(then);
		cells[`pu${p}Rn`] = t.rn - then.rn; cells[`pu${p}Rev`] = minus(t.rev, then.rev); cells[`pu${p}Adr`] = minus(adr(t), adr(then));
		// ROO2-314 Part 3: no new room night shows "–" in both columns (item 2); a total divides its sums (item 3).
		const fresh = news[`D${p}` as PuCut];
		cells[`pu${p}NewRn`] = fresh.rn === 0 ? null : fresh.rn; cells[`pu${p}NewAdr`] = fresh.rn === 0 ? null : bookingAdr(fresh);
	});
	return cells;
};

// ROO2-289 D20: the compset value of the chip: the median or the weighted average of the day values.
export type CompsetChoice = 'MEDIAN' | 'WEIGHTED_AVERAGE';
export type NightCompset = { day: DailyCompsetDay | undefined, choice: CompsetChoice };
const toneOf = (tone: string | undefined): DataTableCellTone | undefined =>
	tone === 'success' || tone === 'warning' || tone === 'critical' ? tone : undefined;
// The difference of the chip as a fraction (+21 % = 0.21); null without an own price or a compset value ("–").
export const compsetDifference = ({ day, choice }: NightCompset): number | null => {
	const difference = (choice === 'WEIGHTED_AVERAGE' ? day?.weightedAverage : day?.median)?.difference;
	return difference === null || difference === undefined ? null : difference / 100;
};

// A night: today in blue, a past night dimmed, a weekend night in bold (D23); ROO2-289: the vs Median chip in the tone of the
// hotel's warning range.
export const nightRow = (night: string, n: NightFigures, rooms: number | null, asOf: string, short = false,
	compset?: NightCompset): DataTableRow => {
	const lead = leadOf(night, asOf);
	const tone = compset === undefined ? undefined
		: toneOf((compset.choice === 'WEIGHTED_AVERAGE' ? compset.day?.weightedAverage : compset.day?.median)?.tone);
	return {
		id: night,
		cells: {
			...figureCells(n, n.avail, n.lyMissing, n.held, n.news, n.ooo), night: nightLabel(night, short), lead: lead < 0 ? null : lead,
			left: lead < 0 || n.avail === null ? null : n.avail - n.D0.rn - n.held,
			diff: compset === undefined ? null : compsetDifference(compset),
		},
		cellTones: tone === undefined ? undefined : { diff: tone },
		state: lead === 0 ? 'current' : (lead < 0 ? 'dimmed' : undefined),
		emphasis: WEEKEND_DAYS.includes(dayOf(night)),
	};
};

// A total (a month row, the footer, the selection): the sums, then the ratios.
export const totalCells = (label: string, s: Sum, rooms: number | null): Cells => ({
	...figureCells(s.cuts, s.avail, s.lyMissing, s.held, s.news, s.ooo), night: label, lead: null, left: s.future > 0 && rooms !== null ? s.left : null, diff: null,
});

// --- the export (B11) -----------------------------------------------------------------------------------------------------
const NOT_EXPORTED = ['diff'];
const DAY_TOKENS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
// The current view as the export query: the shown columns (night first), the hidden days and past nights, the sort and the
// open months. days: the shown days of the week (0 = Sunday); future: the past nights are hidden. ROO2-289: the export holds
// no compset (ROO2-288 B11), so the vs Median column and its sort stay out of the file.
export const exportQuery = (view: { columns: string[], days: number[], future: boolean, sort: DataTableSort | undefined, open: string[] }) => ({
	columns: ['night', ...view.columns.filter(c => c !== 'night' && !NOT_EXPORTED.includes(c))],
	hide: [...[1, 2, 3, 4, 5, 6, 0].filter(d => !view.days.includes(d)).map(d => DAY_TOKENS[d]), ...(view.future ? ['PAST'] : [])],
	sort: view.sort === undefined || NOT_EXPORTED.includes(view.sort.columnId) ? 'none' : `${view.sort.columnId}:${view.sort.direction}`,
	open: view.open,
});
